import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence, useMotionValue, useTransform } from "framer-motion";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Check, X, FileText, Star, AlertCircle, Brain, Loader2, Sparkles, TrendingUp, Briefcase, GraduationCap, Award, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const ScoreSelector = ({ score, onScoreChange }) => (
  <div className="flex items-center gap-1" data-testid="score-selector">
    {[1, 2, 3, 4, 5].map((s) => (
      <button key={s} data-testid={`score-star-${s}`} onClick={() => onScoreChange(s)} className="p-1 transition-colors">
        <Star className={`w-6 h-6 ${s <= score ? "text-amber-400 fill-amber-400" : "text-zinc-600"}`} />
      </button>
    ))}
  </div>
);

const SwipeCard = ({ candidate, onSwipe, isTop, onEvaluate }) => {
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-200, 200], [-8, 8]);
  const acceptOpacity = useTransform(x, [0, 100], [0, 1]);
  const rejectOpacity = useTransform(x, [-100, 0], [1, 0]);

  const [comment, setComment] = useState("");
  const [score, setScore] = useState(3);
  const [showReview, setShowReview] = useState(false);
  const [pendingDecision, setPendingDecision] = useState(null);
  const [evaluating, setEvaluating] = useState(false);

  const parsed = candidate.parsed_data || {};
  const aiEval = candidate.ai_evaluation?.evaluation;
  const displayRole = parsed.current_role || candidate.greenhouse_role || "Candidate";

  const handleDecision = (decision) => { setPendingDecision(decision); setShowReview(true); };
  const submitReview = () => { onSwipe(pendingDecision, score, comment); setShowReview(false); setComment(""); setScore(3); setPendingDecision(null); };
  const handleDragEnd = (e, info) => { if (Math.abs(info.offset.x) > 150) handleDecision(info.offset.x > 0 ? "accept" : "reject"); };

  const handleEvaluate = async () => {
    setEvaluating(true);
    try { await onEvaluate(candidate.candidate_id); } finally { setEvaluating(false); }
  };

  const recommendColors = {
    strongly_recommend: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30",
    recommend: "text-blue-400 bg-blue-500/10 border-blue-500/30",
    maybe: "text-amber-400 bg-amber-500/10 border-amber-500/30",
    not_recommend: "text-red-400 bg-red-500/10 border-red-500/30",
  };

  if (!isTop) return <div className="absolute w-full max-w-2xl h-[72vh] bg-zinc-900 border border-white/5 rounded-2xl shadow-lg scale-[0.97] opacity-50" />;

  return (
    <>
      <motion.div
        data-testid="swipe-card"
        className="absolute w-full max-w-2xl h-[72vh] bg-zinc-900 border border-white/10 rounded-2xl shadow-2xl overflow-hidden cursor-grab active:cursor-grabbing z-10"
        style={{ x, rotate }} drag="x" dragConstraints={{ left: 0, right: 0 }} onDragEnd={handleDragEnd}
        initial={{ scale: 0.97, opacity: 0, y: 15 }} animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.25 } }}
        transition={{ type: "spring", stiffness: 300, damping: 28 }}
      >
        {/* Swipe overlays */}
        <motion.div className="absolute inset-0 bg-emerald-500/15 z-20 flex items-center justify-center pointer-events-none rounded-2xl" style={{ opacity: acceptOpacity }}>
          <div className="px-8 py-4 rounded-xl border-4 border-emerald-400 rotate-[-15deg]"><span className="font-heading text-4xl font-bold text-emerald-400 uppercase">Accept</span></div>
        </motion.div>
        <motion.div className="absolute inset-0 bg-red-500/15 z-20 flex items-center justify-center pointer-events-none rounded-2xl" style={{ opacity: rejectOpacity }}>
          <div className="px-8 py-4 rounded-xl border-4 border-red-400 rotate-[15deg]"><span className="font-heading text-4xl font-bold text-red-400 uppercase">Reject</span></div>
        </motion.div>

        <div className="flex flex-col h-full relative z-10">
          {/* Fixed header */}
          <div className="px-5 pt-4 pb-3 border-b border-white/5 shrink-0">
            <div className="flex items-start justify-between">
              <div className="min-w-0 flex-1">
                <h2 className="font-heading text-xl font-bold text-white uppercase tracking-tight truncate">{candidate.name}</h2>
                <p className="text-sm text-zinc-400">{displayRole}{parsed.current_company ? ` at ${parsed.current_company}` : ""}</p>
                <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                  {candidate.greenhouse_stage && <Badge variant="outline" className="text-[10px] text-blue-400/80 border-blue-500/20">{candidate.greenhouse_stage}</Badge>}
                  {parsed.experience_years > 0 && <Badge variant="secondary" className="font-mono text-[10px] bg-zinc-800 text-zinc-300 border border-white/10">{parsed.experience_years}y exp</Badge>}
                  {candidate.email && <span className="text-[10px] text-zinc-500">{candidate.email}</span>}
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleEvaluate(); }} disabled={evaluating}
                className="h-8 px-2.5 text-violet-400 hover:text-violet-300 hover:bg-violet-500/10 shrink-0" title="Run AI Evaluation">
                {evaluating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                <span className="text-xs ml-1">Evaluate</span>
              </Button>
            </div>
          </div>

          {/* Scrollable content */}
          <ScrollArea className="flex-1 min-h-0">
            <div className="px-5 py-4 space-y-4">

              {/* AI Evaluation */}
              {aiEval && (
                <div className="p-4 rounded-xl bg-violet-500/5 border border-violet-500/20 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Brain className="w-4 h-4 text-violet-400" />
                      <span className="text-xs font-semibold text-violet-300 uppercase tracking-wider">AI Evaluation</span>
                    </div>
                    <span className="font-mono text-lg font-bold text-white">{aiEval.overall_score}<span className="text-zinc-500 text-sm">/10</span></span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    <Badge className={`text-[10px] border ${recommendColors[aiEval.recommendation] || "bg-zinc-700 text-zinc-400"}`}>{aiEval.recommendation?.replace(/_/g, " ")}</Badge>
                    <Badge className={`text-[10px] border ${aiEval.interview_decision === "yes" ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" : aiEval.interview_decision === "conditional" ? "bg-amber-500/10 text-amber-400 border-amber-500/30" : "bg-red-500/10 text-red-400 border-red-500/30"}`}>
                      Interview: {aiEval.interview_decision}
                    </Badge>
                  </div>
                  {aiEval.summary && <p className="text-xs text-zinc-300 leading-relaxed whitespace-pre-line">{aiEval.summary}</p>}
                  {aiEval.strengths?.length > 0 && (
                    <div>
                      <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Strengths</p>
                      <ul className="space-y-0.5">{aiEval.strengths.map((s, i) => <li key={i} className="text-xs text-emerald-400/80 flex items-start gap-1.5"><Check className="w-3 h-3 mt-0.5 shrink-0" />{s}</li>)}</ul>
                    </div>
                  )}
                  {aiEval.concerns?.length > 0 && (
                    <div>
                      <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Concerns</p>
                      <ul className="space-y-0.5">{aiEval.concerns.map((c, i) => <li key={i} className="text-xs text-red-400/80 flex items-start gap-1.5"><X className="w-3 h-3 mt-0.5 shrink-0" />{c}</li>)}</ul>
                    </div>
                  )}
                  {aiEval.competency_scores && Object.keys(aiEval.competency_scores).length > 0 && (
                    <div>
                      <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Competency Scores</p>
                      <div className="flex flex-wrap gap-1.5">
                        {Object.entries(aiEval.competency_scores).map(([k, v]) => (
                          <span key={k} className="text-[10px] px-2 py-0.5 rounded border border-white/10 bg-zinc-800 text-zinc-300 font-mono">{k}: {v}/10</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Summary */}
              {parsed.summary && (
                <div>
                  <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1.5">Summary</p>
                  <p className="text-sm text-zinc-300 leading-relaxed">{parsed.summary}</p>
                </div>
              )}

              {/* Experience & Education row */}
              <div className="grid grid-cols-2 gap-3">
                {parsed.experience_years > 0 && (
                  <div className="p-3 rounded-lg bg-zinc-800/50 border border-white/5">
                    <p className="text-[10px] text-zinc-500 uppercase">Experience</p>
                    <p className="font-mono text-lg text-white">{parsed.experience_years} years</p>
                  </div>
                )}
                {parsed.education && (
                  <div className="p-3 rounded-lg bg-zinc-800/50 border border-white/5">
                    <p className="text-[10px] text-zinc-500 uppercase flex items-center gap-1"><GraduationCap className="w-3 h-3" />Education</p>
                    {Array.isArray(parsed.education) ? parsed.education.map((ed, i) => (
                      <p key={i} className="text-xs text-zinc-300">{ed.degree}{ed.field ? ` in ${ed.field}` : ""}{ed.institution ? ` — ${ed.institution}` : ""}{ed.year ? ` (${ed.year})` : ""}</p>
                    )) : <p className="text-xs text-zinc-300">{parsed.education}</p>}
                  </div>
                )}
              </div>

              {/* Work History */}
              {Array.isArray(parsed.work_history) && parsed.work_history.length > 0 && (
                <div>
                  <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-2 flex items-center gap-1"><Briefcase className="w-3 h-3" />Work History</p>
                  <div className="space-y-3">
                    {parsed.work_history.map((wh, i) => (
                      <div key={i} className="p-3 rounded-lg bg-zinc-800/30 border border-white/5">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="text-sm font-medium text-zinc-200">{wh.title}</p>
                            <p className="text-xs text-zinc-400">{wh.company}</p>
                          </div>
                          {wh.duration && <span className="text-[10px] text-zinc-500 font-mono shrink-0">{wh.duration}</span>}
                        </div>
                        {wh.description && <p className="text-xs text-zinc-500 mt-1.5">{wh.description}</p>}
                        {wh.achievements?.length > 0 && (
                          <ul className="mt-1.5 space-y-0.5">
                            {wh.achievements.map((ach, j) => (
                              <li key={j} className="text-xs text-zinc-400 flex items-start gap-1.5">
                                <TrendingUp className="w-3 h-3 text-blue-400 mt-0.5 shrink-0" />{ach}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Skills */}
              {parsed.skills?.length > 0 && (
                <div>
                  <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1.5">Skills</p>
                  <div className="flex flex-wrap gap-1">
                    {parsed.skills.map((skill, i) => (
                      <span key={i} className="px-2 py-0.5 text-xs rounded bg-zinc-800 text-zinc-300 border border-white/5">{skill}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Certifications */}
              {Array.isArray(parsed.certifications) && parsed.certifications.length > 0 && (
                <div>
                  <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1.5 flex items-center gap-1"><Award className="w-3 h-3" />Certifications</p>
                  <div className="flex flex-wrap gap-1.5">
                    {parsed.certifications.map((c, i) => (
                      <span key={i} className="px-2 py-0.5 text-xs rounded bg-amber-500/10 text-amber-400 border border-amber-500/20">{c}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Highlights */}
              {parsed.highlights?.length > 0 && (
                <div>
                  <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1.5">Key Highlights</p>
                  <ul className="space-y-1">
                    {parsed.highlights.map((h, i) => (
                      <li key={i} className="text-xs text-zinc-400 flex items-start gap-1.5"><TrendingUp className="w-3 h-3 text-blue-400 mt-0.5 shrink-0" />{h}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* No data fallback */}
              {!parsed.summary && !parsed.skills?.length && !parsed.work_history?.length && (
                <div className="flex flex-col items-center justify-center py-8 text-zinc-500">
                  <FileText className="w-8 h-8 mb-2 opacity-50" />
                  <p className="text-sm">Resume not yet parsed</p>
                  {candidate.email && <p className="text-xs text-zinc-600 mt-1">{candidate.email}</p>}
                </div>
              )}

              {/* Bottom padding for scroll */}
              <div className="h-2" />
            </div>
          </ScrollArea>

          {/* Fixed action bar */}
          <div className="px-5 py-3 border-t border-white/5 shrink-0 flex items-center justify-center gap-8">
            <Button data-testid="swipe-reject-btn" onClick={() => handleDecision("reject")} className="w-12 h-12 rounded-full bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-400 p-0" variant="ghost"><X className="w-6 h-6" /></Button>
            <Button data-testid="swipe-accept-btn" onClick={() => handleDecision("accept")} className="w-12 h-12 rounded-full bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 p-0" variant="ghost"><Check className="w-6 h-6" /></Button>
          </div>
        </div>
      </motion.div>

      {/* Review modal */}
      {showReview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="review-modal">
          <div className="w-full max-w-sm bg-zinc-900 border border-white/10 rounded-2xl p-6 space-y-5 animate-slide-up">
            <div className="text-center">
              <div className={`w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-3 ${pendingDecision === "accept" ? "bg-emerald-500/15" : "bg-red-500/15"}`}>
                {pendingDecision === "accept" ? <Check className="w-7 h-7 text-emerald-400" /> : <X className="w-7 h-7 text-red-400" />}
              </div>
              <h3 className="font-heading text-xl font-bold text-white uppercase">{pendingDecision === "accept" ? "Accept" : "Reject"} {candidate.name}?</h3>
            </div>
            <div>
              <label className="text-xs text-zinc-500 uppercase tracking-wider mb-2 block">Rating</label>
              <ScoreSelector score={score} onScoreChange={setScore} />
            </div>
            <div>
              <label className="text-xs text-zinc-500 uppercase tracking-wider mb-2 block">Comment (optional)</label>
              <Textarea data-testid="review-comment" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Why are you accepting/rejecting?" className="bg-zinc-800 border-white/10 text-zinc-200 placeholder:text-zinc-600 resize-none h-20" />
            </div>
            <div className="flex gap-3">
              <Button data-testid="review-cancel-btn" variant="outline" onClick={() => { setShowReview(false); setPendingDecision(null); }} className="flex-1 border-white/10 text-zinc-400 hover:text-white">Cancel</Button>
              <Button data-testid="review-submit-btn" onClick={submitReview} className={`flex-1 ${pendingDecision === "accept" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-red-600 hover:bg-red-700 text-white"}`}>Confirm</Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default function SwipeReview({ user }) {
  const [candidates, setCandidates] = useState([]);
  const [roles, setRoles] = useState([]);
  const [stages, setStages] = useState([]);
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedStage, setSelectedStage] = useState("");
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);

  const fetchCandidates = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedRole && selectedRole !== "all") params.set("role_id", selectedRole);
      if (selectedStage && selectedStage !== "all") params.set("stage", selectedStage);
      const qs = params.toString() ? `?${params.toString()}` : "";
      const resp = await api.get(`/candidates/swipe/next${qs}`);
      setCandidates(resp.data);
      setCurrentIndex(0);
    } catch (e) {
      console.error("Failed to fetch candidates", e);
    } finally {
      setLoading(false);
    }
  }, [selectedRole, selectedStage]);

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const [rolesResp, stagesResp] = await Promise.all([api.get("/roles"), api.get("/stages")]);
        setRoles(rolesResp.data);
        setStages(stagesResp.data);
      } catch (e) {}
    };
    fetchFilters();
  }, []);

  useEffect(() => { fetchCandidates(); }, [fetchCandidates]);

  const handleSwipe = async (decision, score, comment) => {
    const candidate = candidates[currentIndex];
    if (!candidate) return;
    try {
      await api.post("/reviews", { candidate_id: candidate.candidate_id, decision, score, comment });
      toast.success(`${decision === "accept" ? "Accepted" : "Rejected"} ${candidate.name}`);
      if (currentIndex < candidates.length - 1) setCurrentIndex((prev) => prev + 1);
      else fetchCandidates();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit review");
    }
  };

  const handleEvaluate = async (candidateId) => {
    try {
      await api.post(`/candidates/${candidateId}/evaluate`, {});
      toast.success("AI evaluation complete");
      fetchCandidates();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Evaluation failed");
    }
  };

  const currentCandidate = candidates[currentIndex];
  const nextCandidate = candidates[currentIndex + 1];
  const remaining = candidates.length - currentIndex;

  return (
    <div className="px-4 py-6 sm:p-6 md:p-8 lg:p-12 space-y-4 lg:space-y-6 animate-fade-in" data-testid="swipe-review-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white uppercase">Review</h1>
          <p className="text-zinc-500 mt-1 text-sm">Swipe or click to review candidates</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={selectedStage} onValueChange={setSelectedStage}>
            <SelectTrigger className="w-44 bg-zinc-800 border-white/10 text-zinc-300 text-sm" data-testid="stage-filter">
              <SelectValue placeholder="All Stages" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-800 border-white/10">
              <SelectItem value="all">All Stages</SelectItem>
              {stages.map((s) => <SelectItem key={s.name} value={s.name}>{s.name} ({s.count})</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={selectedRole} onValueChange={setSelectedRole}>
            <SelectTrigger className="w-40 bg-zinc-800 border-white/10 text-zinc-300 text-sm" data-testid="role-filter">
              <SelectValue placeholder="All Roles" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-800 border-white/10">
              <SelectItem value="all">All Roles</SelectItem>
              {roles.map((r) => <SelectItem key={r.role_id} value={r.role_id}>{r.title}</SelectItem>)}
            </SelectContent>
          </Select>
          {remaining > 0 && <Badge variant="secondary" className="font-mono bg-zinc-800 border border-white/10 text-zinc-400 text-xs">{remaining} left</Badge>}
        </div>
      </div>

      <div className="flex flex-col items-center justify-center h-[calc(100vh-12rem)] lg:h-[calc(100vh-14rem)] relative overflow-hidden">
        {loading ? (
          <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
        ) : candidates.length === 0 || !currentCandidate ? (
          <div className="text-center space-y-4" data-testid="no-candidates">
            <div className="w-20 h-20 mx-auto rounded-2xl bg-zinc-800 border border-white/10 flex items-center justify-center"><AlertCircle className="w-10 h-10 text-zinc-600" /></div>
            <h3 className="font-heading text-xl font-bold text-zinc-400 uppercase">No candidates to review</h3>
            <p className="text-sm text-zinc-500">Try changing the stage or role filter</p>
          </div>
        ) : (
          <AnimatePresence mode="wait">
            {nextCandidate && <SwipeCard key={`bg-${nextCandidate.candidate_id}`} candidate={nextCandidate} onSwipe={() => {}} isTop={false} onEvaluate={() => {}} />}
            <SwipeCard key={currentCandidate.candidate_id} candidate={currentCandidate} onSwipe={handleSwipe} isTop={true} onEvaluate={handleEvaluate} />
          </AnimatePresence>
        )}
      </div>

      <div className="flex items-center justify-center gap-8 text-xs text-zinc-600">
        <span className="flex items-center gap-1"><ChevronLeft className="w-3 h-3" /> Drag left to reject</span>
        <span className="flex items-center gap-1">Drag right to accept <ChevronRight className="w-3 h-3" /></span>
      </div>
    </div>
  );
}
